import React, { useEffect, useState } from "react";

import { Spin } from "antd";

import MultiLocationSummarySection from "../components/multi-location/MultiLocationSummarySection";
import TestUsageTrend from "../components/multi-location/TestUsageTrend";
import UsageMetricsHowItWorksPanel from "../components/UsageMetricsHowItWorksPanel";
import SubscriptionCategoryUsage from "../subscription/components/SubscriptionCategoryUsage";

import {
  getSubscriptionTierUsage,
  getTotalTopUpAvailableCredits,
  mapCreditConsumptionInsights,
} from "../utils";

import { fetchCreditConsumptionGraph } from "../../../../actions/usage";

import "./PrepaidDashboard.css";

const ALL_LOCATIONS_KEY = "all";

const isTierRunningLow = (tier) => {
  const { used, total, remaining } = getSubscriptionTierUsage(tier, { billingMode: "prepaid" });
  if (remaining <= 0 || total <= 0) return false;
  return used / total >= 0.9;
};

const countRunningLowCategories = (categories) => (
  categories.filter((category) => category.tiers.some(isTierRunningLow)).length
);

export default function UsageMetricsPrepaidDashboard({
  categoryUsage,
  locations,
  selectedLocationKey = ALL_LOCATIONS_KEY,
  onSelectLocation,
  periodLabel,
  onPeriodChange,
  dateRange = "",
  loading = false,
}) {
  const isAllLocations = selectedLocationKey === ALL_LOCATIONS_KEY;
  const selectedLocation = locations.find(
    (loc) => loc.key === selectedLocationKey,
  );
  const graphLocation = isAllLocations
    ? ""
    : selectedLocation?.label;
  const availableCredits = getTotalTopUpAvailableCredits(categoryUsage);
  const [consumedSummary, setConsumedSummary] = useState(null);
  const [consumedSummaryReady, setConsumedSummaryReady] = useState(false);

  const hasCredits = availableCredits > 0;
  const runningLowCount = countRunningLowCategories(categoryUsage);

  let cardState;
  if (!hasCredits) {
    cardState = "empty";
  } else if (runningLowCount >= 2) {
    cardState = "low";
  } else if (runningLowCount === 1) {
    cardState = "lowSingle";
  } else {
    cardState = "ready";
  }

  useEffect(() => {
    setConsumedSummary(null);
    setConsumedSummaryReady(false);
  }, [graphLocation]);

  useEffect(() => {
    const [startDate, endDate] = String(dateRange || "").split(",");
    if (!startDate || !endDate) return undefined;

    const controller = new AbortController();

    const loadConsumedSummary = async () => {
      const response = await fetchCreditConsumptionGraph({
        groupBy: "category",
        location: graphLocation || "",
        startDate,
        endDate,
        signal: controller.signal,
      });

      if (response.canceled) return;

      if (!response.success) {
        setConsumedSummary(null);
        setConsumedSummaryReady(true);
        return;
      }

      const [totalTestsInsight] = mapCreditConsumptionInsights(response, { startDate, endDate });
      setConsumedSummary(totalTestsInsight);
      setConsumedSummaryReady(true);
    };

    loadConsumedSummary();

    return () => controller.abort();
  }, [dateRange, graphLocation]);

  return (
    <div className="UsageMetricsPrepaidDashboard">
      <MultiLocationSummarySection
        totalTests={availableCredits}
        consumedSummary={consumedSummary}
        consumedSummaryReady={consumedSummaryReady}
        locations={locations}
        selectedLocationKey={selectedLocationKey}
        onSelectLocation={onSelectLocation}
        periodLabel={periodLabel}
        onPeriodChange={onPeriodChange}
        loading={loading}
        poolState={cardState}
        runningLowCount={runningLowCount}
      />

      {loading ? (
        <div className="UsageMetricsPrepaidDashboard__FilterLoading">
          <Spin size="large" />
        </div>
      ) : (
        <section className="UsageMetricsPrepaidDashboard__UsageSection">
          <SubscriptionCategoryUsage
            title="Test Category Usage"
            categories={categoryUsage}
            billingMode="prepaid"
          />
          <TestUsageTrend location={graphLocation} />
          <UsageMetricsHowItWorksPanel />
        </section>
      )}
    </div>
  );
}
